<template>
  <div class="search-section">
    <div class="search-container">
      <div class="search-input-wrapper">
        <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <input 
          type="text" 
          placeholder="Search by tutor name or subject..." 
          class="search-input"
          :value="searchQuery"
          @input="$emit('update:searchQuery', $event.target.value)"
          @keyup.enter="handleSearch"
        />
      </div>
      <div class="filter-dropdown-wrapper" ref="subjectDropdownRef">
        <button 
          class="filter-btn subjects-btn" 
          @click="toggleSubjectDropdown"
          :title="selectedSubject || 'All Subjects'"
        >
          <span class="subject-text">
            {{ selectedSubject || 'All Subjects' }}
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9l6 6 6-6"></path>
          </svg>
        </button>
        <div v-if="showSubjectDropdown" class="dropdown-menu">
          <div class="dropdown-item" @click="selectSubject('')">
            All Subjects
          </div>
          <div 
            v-for="subject in availableSubjects" 
            :key="subject" 
            class="dropdown-item"
            @click="selectSubject(subject)"
          >
            {{ subject }}
          </div>
        </div>
      </div>
      <div class="filter-dropdown-wrapper" ref="moreFiltersDropdownRef">
        <button class="filter-btn more-filters-btn" @click="toggleMoreFiltersDropdown">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
          </svg>
          {{ minRating > 0 ? `Min ${minRating}★` : 'More Filters' }}
        </button>
        <div v-if="showMoreFiltersDropdown" class="dropdown-menu">
          <div class="dropdown-item" @click="selectMinRating(0)">
            All Ratings
          </div>
          <div class="dropdown-item" @click="selectMinRating(4)">
            4★ and above
          </div>
          <div class="dropdown-item" @click="selectMinRating(4.5)">
            4.5★ and above
          </div>
          <div class="dropdown-item" @click="selectMinRating(5)">
            5★ only
          </div>
        </div>
      </div>
      <button class="filter-btn clear-btn" @click="handleClear">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"></path>
        </svg>
        Clear
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  searchQuery: {
    type: String,
    default: ''
  },
  selectedSubject: {
    type: String,
    default: ''
  },
  minRating: {
    type: Number,
    default: 0
  },
  availableSubjects: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:searchQuery', 'update:selectedSubject', 'update:minRating', 'search', 'filter', 'clear'])

const showSubjectDropdown = ref(false);
const showMoreFiltersDropdown = ref(false);
const subjectDropdownRef = ref(null);
const moreFiltersDropdownRef = ref(null);

const toggleSubjectDropdown = () => {
  const nextState = !showSubjectDropdown.value;
  showSubjectDropdown.value = nextState;
  if (nextState) {
    showMoreFiltersDropdown.value = false;
  }
};

const toggleMoreFiltersDropdown = () => {
  const nextState = !showMoreFiltersDropdown.value;
  showMoreFiltersDropdown.value = nextState;
  if (nextState) {
    showSubjectDropdown.value = false;
  }
};

const handleSearch = () => {
  const query = props.searchQuery;
  emit('search', query)
}

const selectSubject = (subject) => {
  emit('update:selectedSubject', subject);
  emit('filter', { subject });
  showSubjectDropdown.value = false;
}

const selectMinRating = (rating) => {
  emit('update:minRating', rating);
  emit('filter', { minRating: rating });
  showMoreFiltersDropdown.value = false;
}

const handleClear = () => {
  emit('update:searchQuery', '');
  emit('update:selectedSubject', '');
  emit('update:minRating', 0);
  emit('clear')
}

const handleOutsideClick = (event) => {
  const subjectEl = subjectDropdownRef.value;
  const ratingEl = moreFiltersDropdownRef.value;
  const clickedInsideSubject = subjectEl?.contains(event.target);
  const clickedInsideRatings = ratingEl?.contains(event.target);

  if (!clickedInsideSubject) {
    showSubjectDropdown.value = false;
  }
  if (!clickedInsideRatings) {
    showMoreFiltersDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
});

// Watch for input changes and trigger search
watch(() => props.searchQuery, (newVal) => {
  if (newVal !== undefined) {
    emit('search', newVal);
  }
});
</script>

<style scoped>
.search-section {
  width: 100%;
  margin: 0 auto 50px;
  padding: 0 20px;
  display: flex;
  justify-content: center;
}

.search-container {
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  background: white;
  border: 3px solid #000000;
  border-radius: 12px;
  padding: 40px 35px;
  box-shadow: 2px 2px 0px #000;
  justify-content: space-between;
}

.search-input-wrapper {
  flex: 2;
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #000000;
  border-radius: 10px;
  height: 45px;
  transition: all 0.2s;
  min-width: 350px;
  max-width: 600px;
  box-shadow: 2px 2px 0px #000;
}

.search-input-wrapper:focus-within {
  transform: translateY(-1px);
  box-shadow: 3px 3px 0px #000;
}

.search-icon {
  position: absolute;
  left: 22px;
  color: #6B7280;
  width: 20px;
  height: 20px;
}

.search-input {
  width: 100%;
  padding: 0 24px 0 52px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  outline: none;
  color: #111827;
  background: transparent;
  height: 100%;
}

.search-input::placeholder {
  color: #6B7280;
  font-weight: 400;
}

.filter-btn {
  padding: 0 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  height: 45px;
  border: 2px solid #000000;
  background: white;
  width: auto;
  min-width: 110px;
  justify-content: center;
  color: #111827;
  box-shadow: 2px 2px 0px #000;
}

.filter-btn:hover {
  transform: translateY(-1px);
  box-shadow: 3px 3px 0px #000;
}

.filter-btn svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.subjects-btn {
  margin-left: 1px;
  padding: 0 10px;
  max-width: 150px;
}

.subject-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
  display: inline-block;
  vertical-align: middle;
}

.more-filters-btn {
  margin-left: 0px;
  padding: 0 10px;
  margin-right: 1px;
}

.clear-btn {
  color: #DC2626;
  border-color: #DC2626;
  margin-left: auto;
  padding: 0 15px;
  box-shadow: 2px 2px 0px #DC2626;
  background-color: white;
  min-width: 100px;
}

.clear-btn:hover {
  transform: translateY(-1px);
  box-shadow: 3px 3px 0px #DC2626;
  background-color: #fef2f2;
  color: #DC2626;
}

.clear-btn svg {
  color: #DC2626;
  width: 14px;
  height: 14px;
}

.filter-dropdown-wrapper {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 2px solid #000;
  border-radius: 8px;
  box-shadow: 4px 4px 0px #000;
  z-index: 1000;
  min-width: 250px;
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: #f3f4f6;
}

.dropdown-item:active {
  background: #e5e7eb;
}

/* Responsive Design */
@media (min-width: 768px) {
  .search-container {
    width: 95%;
    max-width: 1100px;
  }
  
  .search-input-wrapper {
    min-width: 350px;
  }
}

@media (min-width: 1024px) {
  .search-input-wrapper {
    min-width: 500px;
  }
}

@media (min-width: 1280px) {
  .search-input-wrapper {
    min-width: 600px;
  }
}

@media (max-width: 767px) {
  .search-input-wrapper {
    min-width: 280px;
    flex: 1;
  }
  
  .search-container {
    gap: 6px;
    padding: 12px;
  }

  .filter-btn {
    padding: 0 8px;
    font-size: 12px;
    min-width: auto;
  }

  .subjects-btn, .more-filters-btn {
    min-width: 90px;
  }

  .clear-btn {
    min-width: 70px;
  }
}

@media (max-width: 640px) {
  .search-container {
    flex-wrap: wrap;
    gap: 8px;
  }

  .search-input-wrapper {
    flex: 1 1 100%;
    min-width: 100%;
  }

  .filter-btn {
    flex: 1 1 auto;
  }
}
</style>
